
import { useState, useEffect } from "react";
import { v4 as uuidv4 } from 'uuid';
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

// Define a consistent Message interface
export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  session_id: string;
  timestamp: string;
}

export const useChatSession = (sessionIdParam: string | null, promptId: string | null, userId: string | null) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(sessionIdParam);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPrompt, setIsLoadingPrompt] = useState(false);
  const [promptContent, setPromptContent] = useState<string | null>(null);
  const [promptTitle, setPromptTitle] = useState<string | null>(null);
  const [isSavingToFavorites, setIsSavingToFavorites] = useState(false);
  const { toast } = useToast();

  // Load existing session
  useEffect(() => {
    if (sessionIdParam) {
      setSessionId(sessionIdParam);
      loadExistingSession(sessionIdParam);
    }
  }, [sessionIdParam]);

  // Load prompt when promptId changes
  useEffect(() => {
    if (promptId) {
      loadPromptContent(promptId);
    } else {
      setPromptContent(null);
      setPromptTitle(null);
    }
  }, [promptId]);

  const loadExistingSession = async (sessionId: string) => {
    setIsLoading(true);
    try {
      // First, get session details
      const { data: sessionData, error: sessionError } = await supabase
        .from('product_chat_sessions')
        .select('id, title, user_id, template_id')
        .eq('id', sessionId)
        .single();

      if (sessionError) throw sessionError;

      // If this session has a template, load its details
      if (sessionData.template_id) {
        const { data: promptData, error: promptError } = await supabase
          .from('prompts')
          .select('title, content')
          .eq('id', sessionData.template_id)
          .single();

        if (!promptError && promptData) {
          setPromptContent(promptData.content);
          setPromptTitle(promptData.title);
        }
      }

      // Now load all messages from this session
      const { data: messagesData, error: messagesError } = await supabase
        .from('product_chat_messages')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (messagesError) throw messagesError;

      if (messagesData) {
        // Convert database message format to our ChatMessage format
        const formattedMessages: ChatMessage[] = messagesData.map(msg => ({
          id: msg.id,
          role: msg.role as "user" | "assistant" | "system",
          content: msg.content,
          session_id: msg.session_id,
          timestamp: msg.created_at
        }));
        
        setMessages(formattedMessages);
      }
    } catch (error) {
      console.error('Error loading session:', error);
      toast({
        title: "Error",
        description: "Failed to load chat session",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadPromptContent = async (id: string) => {
    setIsLoadingPrompt(true);
    try {
      const { data, error } = await supabase
        .from('prompts')
        .select('title, content')
        .eq('id', id)
        .single();

      if (error) throw error;

      setPromptContent(data.content);
      setPromptTitle(data.title);

      // Clear old messages when starting with a new prompt
      setMessages([]);
      // Reset session ID when starting with a new prompt
      setSessionId(null);

      // Create a welcome message from the assistant
      const welcomeMessage: ChatMessage = {
        id: uuidv4(),
        role: "system",
        content: `I'll help you with ${data.title}. What would you like to know?`,
        session_id: "",
        timestamp: new Date().toISOString()
      };

      setMessages([welcomeMessage]);
    } catch (error: any) {
      console.error('Error loading prompt:', error);
      toast({
        title: "Error",
        description: "Failed to load prompt content",
        variant: "destructive"
      });
    } finally {
      setIsLoadingPrompt(false);
    }
  };

  const createNewSession = async (firstMessage: string, isTemplateChat: boolean): Promise<string> => {
    if (!userId) throw new Error("User must be logged in to create a session");

    try {
      // Create a new session
      const { data, error } = await supabase
        .from('product_chat_sessions')
        .insert({
          user_id: userId,
          title: promptTitle || "New Conversation",
          template_id: isTemplateChat ? promptId : null
        })
        .select()
        .single();

      if (error) throw error;

      const newSessionId = data.id;
      setSessionId(newSessionId);

      // If we have a system message, save it first
      if (messages.length > 0 && messages[0].role === "system") {
        await supabase
          .from('product_chat_messages')
          .insert({
            session_id: newSessionId,
            role: messages[0].role,
            content: messages[0].content,
            user_id: userId
          });
      }

      return newSessionId;
    } catch (error) {
      console.error("Error creating new session:", error);
      throw error;
    }
  };

  const sendMessage = async (message: string) => {
    if (!message.trim()) return;
    if (!userId) {
      toast({
        title: "Authentication Required",
        description: "Please log in to use the chat feature",
        variant: "destructive"
      });
      return;
    }

    // Prepare user message
    const userMessage: ChatMessage = {
      id: uuidv4(),
      role: "user",
      content: message,
      session_id: sessionId || "",
      timestamp: new Date().toISOString()
    };

    // Optimistically update UI with user message
    setMessages((prev) => [...prev, userMessage]);
    
    // Show loading state
    setIsLoading(true);

    try {
      let currentSessionId = sessionId;
      const isTemplateChat = !!promptId;

      // If no session exists, create one
      if (!currentSessionId) {
        currentSessionId = await createNewSession(message, isTemplateChat);
        userMessage.session_id = currentSessionId;
      }

      // Save user message to database
      const { error: messageError } = await supabase
        .from('product_chat_messages')
        .insert({
          session_id: currentSessionId,
          role: "user",
          content: message,
          user_id: userId
        });

      if (messageError) throw messageError;

      // Call OpenAI API via Supabase Edge Function
      const { data, error } = await supabase.functions.invoke("openai-chat", {
        body: {
          userMessage: message,
          promptTemplate: promptContent,
          sessionId: currentSessionId,
          history: messages.map(m => ({
            role: m.role,
            content: m.content
          }))
        },
      });

      if (error) throw error;

      // Prepare AI response message
      const aiMessage: ChatMessage = {
        id: uuidv4(),
        role: "assistant",
        content: data.message,
        session_id: currentSessionId,
        timestamp: new Date().toISOString()
      };

      // Add AI message to UI
      setMessages((prev) => [...prev, aiMessage]);

      // Save AI message to database
      await supabase
        .from('product_chat_messages')
        .insert({
          session_id: currentSessionId,
          role: "assistant",
          content: data.message,
          user_id: userId
        });

    } catch (error: any) {
      console.error("Error in chat:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to get a response. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveToFavorites = async () => {
    if (!userId || !promptId) return;

    setIsSavingToFavorites(true);
    try {
      // Check if already saved
      const { data, error } = await supabase
        .from('user_saved_prompts')
        .select('id')
        .eq('user_id', userId)
        .eq('prompt_id', promptId)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        // Already saved, so remove from favorites
        const { error: deleteError } = await supabase
          .from('user_saved_prompts')
          .delete()
          .eq('id', data.id);

        if (deleteError) throw deleteError;

        toast({
          description: "Removed from favorites",
        });
      } else {
        // Not saved, so add to favorites
        const { error: insertError } = await supabase
          .from('user_saved_prompts')
          .insert({
            user_id: userId,
            prompt_id: promptId
          });

        if (insertError) throw insertError;

        toast({
          description: "Added to favorites",
        });
      }
    } catch (error) {
      console.error("Error saving to favorites:", error);
      toast({
        title: "Error",
        description: "Failed to update favorites",
        variant: "destructive"
      });
    } finally {
      setIsSavingToFavorites(false);
    }
  };

  return {
    messages,
    isLoading,
    isLoadingPrompt,
    sessionId,
    promptContent,
    promptTitle,
    isSavingToFavorites,
    sendMessage,
    handleSaveToFavorites
  };
};
